import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000HandleApiError } from '../../u2000Core/axios/U2000HandleApiError';
import { NS } from '../U3202';
import {
  doTestCatalogModel,
  luHandleAppiError,
  type U3202ModelTestResponse,
  type U3202OrgModelConfigGridRow,
  type U3202OrgProviderConfigFormData,
} from '../U3202Service';
import { u3202BuildModelTestRequest } from '../utils/u3202ModelTestUtils';

interface UseU3202TestModelHandlersOptions {
  parentData: U3202OrgProviderConfigFormData | null;
}

export interface U3202ModelTestAlert {
  type: 'success' | 'error';
  title: string;
  message: string;
}

export function useU3202TestModelHandlers({ parentData }: UseU3202TestModelHandlersOptions) {
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const [testing, setTesting] = useState(false);
  const [testAlert, setTestAlert] = useState<U3202ModelTestAlert | null>(null);

  const clearTestAlert = useCallback(() => setTestAlert(null), []);

  const buildTestAlert = useCallback(
    (result: U3202ModelTestResponse): U3202ModelTestAlert => {
      const title = t('catalogModel.testTitle', { ns: NS, model: result.modelName });
      if (result.success) {
        return {
          type: 'success',
          title,
          message: t('catalogModel.testSuccess', {
            ns: NS,
            preview: result.responsePreview ?? 'OK',
            latencyMs: result.latencyMs,
          }),
        };
      }
      return {
        type: 'error',
        title,
        message: t('catalogModel.testFailed', {
          ns: NS,
          message: result.errorMessage ?? t('catalogModel.testFailedUnknown', { ns: NS }),
          latencyMs: result.latencyMs,
        }),
      };
    },
    [t],
  );

  const testSelectedModel = useCallback(
    async (row: U3202OrgModelConfigGridRow) => {
      if (!parentData) {
        return;
      }
      setTesting(true);
      setTestAlert(null);
      try {
        const response = await doTestCatalogModel(
          u2000AxiosAuthenticated,
          u3202BuildModelTestRequest(parentData, row),
        );
        const result = response.data?.result as U3202ModelTestResponse;
        setTestAlert(buildTestAlert(result));
      } catch (error) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
        const message =
          errorResult.genericErrors?.map((err) => err.text).join(' ') ||
          t('catalogModel.testFailedUnknown', { ns: NS });
        setTestAlert({
          type: 'error',
          title: t('catalogModel.testTitle', { ns: NS, model: row.modelName }),
          message,
        });
      } finally {
        setTesting(false);
      }
    },
    [buildTestAlert, parentData, t, u2000AxiosAuthenticated],
  );

  return { testing, testAlert, testSelectedModel, clearTestAlert };
}
